import { Fragment, useMemo } from 'react'
import { css } from '@emotion/react'
import {
  AccordionPromo,
  DesignToken,
  Heading,
  Icon,
  type IconSource,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'

import type { ScopeGroup } from '@/utils/auth/interfaces'
import { withVar } from '@/utils/styles'

const labelCss = css`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'icon heading'
    '. scopes';
  column-gap: var(${SpacingToken.Spacing2});
`

const iconCss = css`
  grid-area: icon;
  align-items: flex-start;
`

const headingCss = css`
  grid-area: heading;
  margin: 0 0 var(${SpacingToken.Spacing1});
`

const scopeNamesCss = css`
  grid-area: scopes;
  margin: 0;
  color: var(${DesignToken.ContentBasicSecondary});
`

const contentCss = css`
  display: grid;
  gap: var(${SpacingToken.Spacing3}) var(${SpacingToken.Spacing2});
  grid-template-columns: fit-content(110px) 1fr;
  padding-left: var(${SpacingToken.Spacing8});
  margin-top: calc(var(${SpacingToken.Spacing1}) * -1);

  p {
    margin: 0;
  }
`

interface Props {
  category: string
  scopeGroups: ScopeGroup[]
  icon: IconSource
}

export const ScopesAccordion = ({ category, scopeGroups, icon }: Props) => {
  const scopeGroupsNames = useMemo(
    () => scopeGroups.map(({ name }) => name),
    [scopeGroups]
  )

  if (!scopeGroups.length) {
    return null
  }

  return (
    <AccordionPromo
      label={
        <div css={labelCss} data-test="scopes-view">
          <Icon size="large" css={iconCss} source={icon} />
          <Heading css={headingCss} size="xs">
            {category}
          </Heading>
          <Text size="sm" css={scopeNamesCss}>
            {scopeGroupsNames.join(', ')}
          </Text>
        </div>
      }
    >
      <div css={contentCss} data-test="scopes-view-expanded">
        {scopeGroups.map(({ name, scopes }) => (
          <Fragment key={name}>
            <Text bold>{name} </Text>
            <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>
              {scopes.join(', ')}
            </Text>
          </Fragment>
        ))}
      </div>
    </AccordionPromo>
  )
}
